import type { CommunicationTemplateData } from "@/lib/types";
import { formatPrice } from "@/lib/utils";

/**
 * Default communication message templates
 */
export const DEFAULT_COMMUNICATION_TEMPLATES = {
  listingRejection: `Hello {sellerName},

I'm Elizabeth from Sell It Off. Thank you for trusting us with your "{itemTitle}" priced at {itemPrice}.

After careful review, your listing is pending approval in its current state... it doesn't fully meet our marketplace standards *yet*. This decision helps us maintain the quality and trust that our buyers expect.

Please address the following:
{feedback_summary}

Please update your listing and we will review it again. We're on standby to help if you need it.

Best regards,
Elizabeth,
Sell It Off Team.`,

  listingAcceptance: `🥳 Congrats, {sellerName}!

Your item *{itemTitle}* has been approved and is now visible to potential buyers in your area on the Sell It Off marketplace! 

Here's what happens next:
* Your item is searchable and discoverable by our buyers
* We'll notify you via WhatsApp immediately when someone pays for your item
* You can share your listing link to reach even more buyers

To maximize your item's success on Sell It Off:
* Respond promptly when we reach out to you
* If your item gets sold elsewhere, please update it as sold on the platform or inform us on WhatsApp
* Ensure your listing describes the exact condition of the item and includes all the details

We're here to support you throughout the selling process. Thank you for choosing Sell It Off!

Best regards,
Elizabeth,
Sell It Off Team`,

  paymentReceived: `🎉 Congrats, {sellerName}!

We've just received payment for your item "{itemTitle}" - congrats on your sale!

Here's what happens next:
📞 The buyer will contact you shortly via WhatsApp or phone call to arrange pickup
📍 Please ensure the item is ready and in the exact condition described in your listing
⏰ Coordinate a convenient pickup time and location (you'd soon be able to do this on the app)
✅ Once pickup is complete, confirm with us via WhatsApp (you'd soon be able to do this on the app)

Important reminders:
* Be respectful to our buyers
* Let us know through WhatsApp immediately after successful pickup (you'd soon be able to do this on the app)
* Be available for the agreed pickup time and location
* Meet at open locations with other people around

Your payment will be processed almost immediately after the pickup is by you and the buyer.

Thank you for being a valued member of the Sell It Off community!

Best regards,
Elizabeth,
Sell It Off Team`,

  pickupComplete: `Hello {sellerName},

I hope the pickup for "{itemTitle}" went smoothly!

To complete this transaction and process your payment, please
* Confirm if the item has been picked up by the buyer.
* Send in your bank account details for the payment to be processed.

Once you confirm pickup completion, we'll process your payment immediately directly to your account.

Simply reply "PICKUP CONFIRMED" to this message, and also give us feedback on your experience.

Thank you for choosing Sell It Off. We hope you had a great selling experience and look forward to helping you with future listings!

Best regards,
Elizabeth,
Sell It Off Team`,
};

/**
 * Available placeholders for communication templates
 */
export const COMMUNICATION_TEMPLATE_PLACEHOLDERS = {
  "{sellerName}": "Seller's name (falls back to 'there' if not available)",
  "{itemTitle}": "Item title",
  "{itemPrice}": "Formatted item price (e.g., ₦10,000)",
  "{itemLocation}": "Item location",
  "{itemDescription}": "Item description",
  "{itemDefects}": "Item defects",
  "{feedback_summary}":
    "Structured improvement bullets (same as seller sees in-app); optional",
};

/**
 * Replaces placeholders in a communication template with actual values
 */
export function applyCommunicationTemplate(
  template: string,
  data: CommunicationTemplateData,
): string {
  let result = template;

  // Replace all placeholders using specific known placeholders (handled below)
  // The generic placeholder replacement is handled by the specific handlers below

  // Handle specific known placeholders with formatting
  if (data.sellerName) {
    result = result.replace(/\{sellerName\}/g, data.sellerName);
  } else {
    result = result.replace(/\{sellerName\}/g, "there");
  }

  if (data.itemTitle) {
    result = result.replace(/\{itemTitle\}/g, data.itemTitle);
  }

  if (data.itemPrice !== undefined) {
    const formattedPrice = formatPrice(data.itemPrice);
    result = result.replace(/\{itemPrice\}/g, formattedPrice);
  }

  if (data.itemLocation) {
    result = result.replace(/\{itemLocation\}/g, data.itemLocation);
  }

  if (data.itemDescription) {
    result = result.replace(/\{itemDescription\}/g, data.itemDescription);
  }

  if (data.itemDefects) {
    result = result.replace(/\{itemDefects\}/g, data.itemDefects);
  }

  const feedbackSummary =
    typeof data.feedbackSummary === "string" ? data.feedbackSummary : "";
  result = result.replace(/\{feedback_summary\}/g, feedbackSummary);
  result = result.replace(/\{selectedIssues\}/g, feedbackSummary);

  return result;
}

/**
 * Gets the default template for a message type
 */
export function getDefaultCommunicationTemplate(
  messageType: keyof typeof DEFAULT_COMMUNICATION_TEMPLATES,
): string {
  return DEFAULT_COMMUNICATION_TEMPLATES[messageType] || "";
}

/**
 * Generates a communication message using the configured template
 */
export function generateCommunicationMessage(
  template: string,
  data: CommunicationTemplateData,
): string {
  return applyCommunicationTemplate(template, data);
}
